import {Box, Button, CircularProgress, Stack, Typography} from "@mui/material";
import {AuthActions} from "crqlar-react-common/dist/state/auth.reducer";
import {AuthService} from "crqlar-react-common/dist/services/auth/auth.service";
import {Settings} from "luxon";
import {useCallback, useEffect, useRef, useState} from "react";
import {useDispatch} from "react-redux";
import {useNavigate, useSearchParams} from "react-router-dom";
import {syncMixpanelAuth} from "../../utils/analytics/mixpanelAuth";
import {exchangePosDeviceKey, isSafePosLandingPath, type PosSession} from "./posSession.service";
import {enterPosMode} from "./posMode";

/**
 * Till sign-in for a POS that opens crqlar in a browser it wipes on every launch.
 *
 * Oracle's Simphony **Custom Browser** gives each launch a fresh browser profile, so no cookie or
 * `localStorage` session survives between opens — verified on the sandbox workstation on
 * 2026-09-18, where every tap landed on the login screen. The credential therefore lives in the URL
 * that Simphony's EMC stores:
 *
 * ```
 * https://hotel.crqlar.com/pos?k=<device key>
 * ```
 *
 * This page exchanges that key for an ordinary crqlar session and forwards to the till's configured
 * landing page — a specific restaurant's floor plan, in practice. No staff member types anything.
 *
 * The key is replaced in the address bar by the redirect, so it does not linger on screen or in the
 * session's history. It is **not** a secret from the property: anyone with EMC access reads it
 * there, which is why it resolves to a restricted service user rather than a person's account.
 *
 * See `features/simphony-pos-till-authentication.md`.
 */
export default function PosTillLogin() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const [error, setError] = useState<string | undefined>();
    // React 18 mounts effects twice in development; without this the key is exchanged twice and the
    // second attempt races the redirect.
    const startedRef = useRef(false);

    const key = searchParams.get("k")?.trim();
    const workstation = searchParams.get("workstation")?.trim() || undefined;
    /**
     * Destination override, for setting a till up before the device has a landing page configured.
     * Same rules as the server applies to a stored landing path: a dashboard path, never a URL.
     */
    const landingOverride = searchParams.get("to")?.trim();

    const signIn = useCallback(async () => {
        setError(undefined);

        if (!key) {
            setError("This till is not configured. Its link is missing the device key.");
            return;
        }

        try {
            // Drop whatever the previous session left behind, so a stale token cannot win over the
            // key the till was just launched with.
            localStorage.removeItem("token");

            const session: PosSession = await exchangePosDeviceKey(key, workstation);
            localStorage.setItem("token", session.token);

            // Mirrors the admin-SSO path in App.tsx: App's own token validation has already run by
            // the time this page mounts, so the session has to be established here rather than
            // waiting for a reload that never comes.
            const claims = await AuthService.validateToken();
            if (!claims?.token || !claims.user) {
                localStorage.removeItem("token");
                setError("This till's crqlar user is no longer active. Ask your manager to check it.");
                return;
            }

            localStorage.setItem("token", claims.token);
            dispatch({
                type: AuthActions.SET_USER,
                payload: {user: claims.user, organization: claims.organization},
            });
            syncMixpanelAuth(claims);
            Settings.defaultZone = (claims.organization?.address?.timeZone as string) ?? "Europe/Vienna";

            const target = (landingOverride && isSafePosLandingPath(landingOverride) ? landingOverride : undefined)
                ?? (session.landingPath && isSafePosLandingPath(session.landingPath) ? session.landingPath : undefined)
                ?? "/";

            // Set before navigating: the dashboard layout reads it when it mounts for the landing page.
            enterPosMode();

            // `replace` so the key is gone from the address bar and from history.
            navigate(target, {replace: true});
        } catch (e) {
            console.error("POS till sign-in failed", e);
            setError("crqlar could not sign this till in. Its key may have been replaced.");
        }
    }, [dispatch, key, landingOverride, navigate, workstation]);

    useEffect(() => {
        if (startedRef.current) return;
        startedRef.current = true;
        void signIn();
    }, [signIn]);

    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minHeight: "100dvh",
                p: 4,
                textAlign: "center",
            }}
        >
            {error ? (
                <Stack spacing={3} alignItems="center" maxWidth={520}>
                    <Typography variant="h6">{error}</Typography>
                    {/* Oversized on purpose: this is read at arm's length on a till, by someone
                        mid-service who cannot open a console. */}
                    <Button
                        variant="contained"
                        size="large"
                        sx={{minHeight: 64, minWidth: 220, fontSize: 20}}
                        onClick={() => {
                            startedRef.current = true;
                            void signIn();
                        }}
                    >
                        Try again
                    </Button>
                </Stack>
            ) : (
                <Stack spacing={3} alignItems="center">
                    <CircularProgress/>
                    <Typography variant="body1">Signing in…</Typography>
                </Stack>
            )}
        </Box>
    );
}
